import React, { useState, useRef, useEffect } from "react";
import { Avatar, Badge, IconButton } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import NotificationsIcon from '@mui/icons-material/Notifications';

import AccountDropdown from "../AccountDropdown/AccountDropdown";
import NotificationDropdown from "../NotificationDropdown/NotificationDropdown";
import VenueClock from "../VenueClock/VenueClock";
import logo from "../../assets/logo/square.png";

import {
  NavBar,
  StyledToolbar,
  InfoContainer,
  Title,
  AvatarContainer,
  NotificationIconContainer
} from "./TopBar.styles";

import { getUnreadCountThunk, selectUnreadCount } from "../../redux/notifications/notificationsSlice";

const TopBar = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.currentUser);
  const unreadCount = useSelector(selectUnreadCount);
  const employeeId = currentUser?.employeeInfo?.id;

  // local state for dropdown
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);

  // refs to close dropdowns when clicked outside
  const dropdownRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  const notificationIconRef = useRef(null);
  const NOTIFICATION_POLL_INTERVAL_MS = 60000; // 1 minute

  // fetch unread initial count initially and poll every minute
  useEffect(() => {
    if (employeeId) {
      dispatch(getUnreadCountThunk(employeeId));

      const interval = setInterval(() => {
        dispatch(getUnreadCountThunk(employeeId));
      }, NOTIFICATION_POLL_INTERVAL_MS);

      return () => clearInterval(interval); //clean on unmount
    }
  }, [dispatch, employeeId]);

  // close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        event.target.tagName !== "IMG"
      ) {
        setDropdownOpen(false);
      }
      if (
        notificationDropdownRef.current &&
        !notificationDropdownRef.current.contains(event.target) &&
        !notificationIconRef.current?.contains(event.target)
      ) {
        setNotificationDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleAvatarClick = () => {
    setDropdownOpen((prev) => !prev);
    setNotificationDropdownOpen(false);
  };

  const handleNotificationClick = () => {
    setNotificationDropdownOpen((prev) => !prev);
    setDropdownOpen(false);
  };

  const handleCloseDropdown = () => {
    setDropdownOpen(false);
  };

  const handleCloseNotifications = () => {
    setNotificationDropdownOpen(false);
  };

  const boxVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  return (
    <NavBar>
      <StyledToolbar>
        <InfoContainer>
          <img src={logo} alt="Tempo Logo" style={{ width: '40px', height: '40px' }} />
          <Title>Tempo</Title>
        </InfoContainer>
        <AvatarContainer>
          <VenueClock />

          <NotificationIconContainer>
            <IconButton
              ref={notificationIconRef}
              onClick={handleNotificationClick}
              aria-label="Notifications"
            >
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <AnimatePresence>
              {notificationDropdownOpen && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={boxVariants}
                  transition={{ duration: 0.3 }}
                  ref={notificationDropdownRef}
                >
                  <NotificationDropdown onClose={handleCloseNotifications} />
                </motion.div>
              )}
            </AnimatePresence>
          </NotificationIconContainer>

          <Avatar
            alt={currentUser?.employeeInfo?.displayName || "User"}
            src={currentUser?.employeeInfo?.profileImageUrl}
            onClick={handleAvatarClick}
            style={{ cursor: "pointer", justifySelf: 'center' }}
          />

          <AnimatePresence>
            {dropdownOpen && (
              <motion.div
                className="dropdown-container"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={boxVariants}
                transition={{ duration: 0.3 }}
                ref={dropdownRef}
              >
                <AccountDropdown onSignOut={handleCloseDropdown} />
              </motion.div>
            )}
          </AnimatePresence>
        </AvatarContainer>
      </StyledToolbar>
    </NavBar>
  );
};

export default TopBar;